import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { formatMinor } from "@/lib/hooks";
import { useDrivers } from "@/components/TaskManager";
import { WhatsAppSend } from "@/components/WhatsAppSend";
import { Button, Card, ErrorNote, Modal, Spin, Spinner, StatusBadge } from "@/components/ui";
import { ConfirmButton } from "@/components/ConfirmButton";
import { EditBookingLineModal } from "@/components/EditBookingLineModal";
import { LineFields } from "@/components/LineFields";
import { Labeled, Select, TextField } from "@/components/Form";
import i18n, { tServiceKind, tStatus } from "@/i18n";
import { companyToday, formatDateOnly, formatZonedTime } from "@/lib/time";
import {
  driverPayField,
  emptyLineDraft,
  fromMinor,
  isOwnDriver,
  lineDraftIsValid,
  lineDraftToPayload,
  toMinor,
} from "@/lib/bookingLines";
import type { BookingLineCostCategory, BookingLineDetail } from "@/lib/types";

const COST_CATEGORIES: BookingLineCostCategory[] = [
  "fuel",
  "parking",
  "driver_contractor",
  "entrance_tickets",
  "other",
];

function LineCosts({
  bookingRef,
  line,
  canWrite,
}: {
  bookingRef: string;
  line: BookingLineDetail;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    category: "fuel" as BookingLineCostCategory,
    amount: "",
    currency: line.currency,
    supplier_name: "",
    incurred_at: "",
  });
  // ["booking", ref] carries the booking's money card (sale / cost / profit /
  // paid / balance), so it follows a new / voided cost straight away.
  const invalidate: unknown[][] = [["booking-lines", bookingRef], ["booking", bookingRef]];
  const add = useEntityAction(invalidate, { inlineError: true,
    onSuccess: () => {
      setOpen(false);
      setF((prev) => ({ ...prev, amount: "", supplier_name: "" }));
    },
  });
  const voidCost = useEntityAction(invalidate, { successMessage: t("common.deleted") });
  const expenseAction = useEntityAction(invalidate);
  // which approve / reject button is in flight (they share one action)
  const [busyExpense, setBusyExpense] = useState<string | null>(null);
  const settle = { onSettled: () => setBusyExpense(null) };
  // The automatic supplier row at 0 (our own driver, nothing to pay) is noise.
  const shownCosts = line.costs.filter((c) => !(c.is_auto_supplier_cost && c.amount_minor === 0));

  return (
    <div className="mt-2 border-t border-olive-700 pt-2">
      <div className="flex items-center gap-2">
        <h4 className="text-xs font-semibold text-sand-muted">
          {t("bookingLines.costs")}
        </h4>
        {canWrite && !open && (
          <button
            className="ml-auto text-xs text-gold hover:underline"
            onClick={() => setOpen(true)}
          >
            + {t("bookingLines.addCost")}
          </button>
        )}
      </div>
      {shownCosts.length === 0 ? (
        <p className="mt-1 text-xs text-sand-muted">{t("bookingLines.noCosts")}</p>
      ) : (
        <ul className="mt-1 divide-y divide-olive-700 text-xs">
          {shownCosts.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 py-1">
              <span className={c.deleted_at ? "text-sand-muted line-through" : ""}>
                {lineCostLabel(c.category, line.service_kind, t)}: {formatMinor(c.amount_minor, c.currency)}
                {c.supplier_name ? ` · ${c.supplier_name}` : ""}
              </span>
              {c.status !== "confirmed" && <StatusBadge status={c.status} />}
              {c.receipt_photo_url && (
                <a
                  className="text-gold hover:underline"
                  href={c.receipt_photo_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("bookingLines.viewReceipt")}
                </a>
              )}
              {canWrite && c.status === "under_review" && (
                <span className="ml-auto flex gap-2">
                  <button
                    className="text-emerald-700 hover:underline disabled:opacity-60"
                    disabled={expenseAction.isPending}
                    aria-busy={busyExpense === `${c.id}:approve` || undefined}
                    onClick={() => {
                      setBusyExpense(`${c.id}:approve`);
                      expenseAction.mutate(
                        () =>
                          api.post(
                            `/bookings/${bookingRef}/lines/${line.id}/costs/${c.id}/approve`,
                          ),
                        settle,
                      );
                    }}
                  >
                    {busyExpense === `${c.id}:approve` && <Spin className="mr-1.5" />}
                    {t("bookingLines.approveExpense")}
                  </button>
                  <button
                    className="text-red-700 hover:underline disabled:opacity-60"
                    disabled={expenseAction.isPending}
                    aria-busy={busyExpense === `${c.id}:reject` || undefined}
                    onClick={() => {
                      setBusyExpense(`${c.id}:reject`);
                      expenseAction.mutate(
                        () =>
                          api.post(
                            `/bookings/${bookingRef}/lines/${line.id}/costs/${c.id}/reject`,
                          ),
                        settle,
                      );
                    }}
                  >
                    {busyExpense === `${c.id}:reject` && <Spin className="mr-1.5" />}
                    {t("bookingLines.rejectExpense")}
                  </button>
                </span>
              )}
              {canWrite && !c.deleted_at && c.status !== "under_review" && (
                <button
                  className="ml-auto text-red-700 hover:underline disabled:opacity-60"
                  disabled={voidCost.isPending}
                  aria-busy={voidCost.isPending || undefined}
                  onClick={() =>
                    voidCost.mutate(() =>
                      api.del(
                        `/bookings/${bookingRef}/lines/${line.id}/costs/${c.id}`,
                      ),
                    )
                  }
                >
                  {voidCost.isPending && <Spin className="mr-1.5" />}
                  {t("finance.void")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            aria-label={t("bookingLines.costCategory")}
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value as BookingLineCostCategory })}
          >
            {COST_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {tStatus(c)}
              </option>
            ))}
          </Select>
          <TextField
            aria-label={t("bookingLines.costAmount")}
            type="number"
            min="0"
            step="0.01"
            placeholder={`${t("bookingLines.costAmount")}, ${line.currency}`}
            value={f.amount}
            onChange={(e) => setF({ ...f, amount: e.target.value })}
          />
          <TextField
            placeholder={t("bookingLines.costSupplier")}
            value={f.supplier_name}
            onChange={(e) => setF({ ...f, supplier_name: e.target.value })}
          />
          <div className="flex gap-2">
            <Button
              className="px-3 py-1 text-xs"
              loading={add.isPending}
              disabled={toMinor(f.amount) === null || toMinor(f.amount) === 0}
              onClick={() =>
                add.mutate(() =>
                  api.post(`/bookings/${bookingRef}/lines/${line.id}/costs`, {
                    category: f.category,
                    amount_minor: toMinor(f.amount),
                    currency: f.currency,
                    supplier_name: f.supplier_name || null,
                  }),
                )
              }
            >
              {t("common.save")}
            </Button>
            <button
              className="text-xs text-sand-muted hover:text-sand"
              onClick={() => setOpen(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {add.isError && (
        <div className="mt-1">
          <ErrorNote message={(add.error as Error).message} />
        </div>
      )}
    </div>
  );
}

/** 80000 → "800", 110175 → "1 101,75" (minor units, no currency) — in the
 * page's language, like every other figure on it. */
function plainAmount(minor: number): string {
  return new Intl.NumberFormat(i18n.language === "ru" ? "ru-RU" : "en-US", {
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

const FORMULA_COST_ORDER = [
  "driver_contractor",
  "guide",
  "fuel",
  "parking",
  "entrance_tickets",
  "other",
];

/** The line's money in one plain sentence: «Продажа 800 − бензин 100 = прибыль
 * 700 AED» — every cost of the line by name, then the agent commission and the
 * extra cost attached to it (owner rules 18-19), all AED; zero terms left out. */
export function lineFormula(line: BookingLineDetail, t: (k: string) => string): string {
  const m = line.money;
  const f = (k: string) => t(`bookingLines.formula.${k}`);
  const costLabel = (category: string) => {
    if (category === "driver_contractor") {
      if (line.service_kind === "yacht") return f("yacht");
      if (line.service_kind === "guide") return f("guide");
    }
    if (category === "entrance_tickets" && line.service_kind === "park_tickets") {
      return f("ticketSupplier");
    }
    return FORMULA_COST_ORDER.includes(category) ? f(category) : tStatus(category);
  };
  const byCategory = m.costs_by_category_aed ?? {};
  const categories = [
    ...FORMULA_COST_ORDER.filter((c) => c in byCategory),
    ...Object.keys(byCategory).filter((c) => !FORMULA_COST_ORDER.includes(c)),
  ];
  const sale =
    line.currency === "AED"
      ? plainAmount(m.sale_aed_minor)
      : `${plainAmount(m.sale_aed_minor)} (${formatMinor(m.sale_native_minor, line.currency)})`;
  const terms = [`${f("sale")} ${sale}`];
  for (const c of categories) {
    if (byCategory[c] > 0) terms.push(`${costLabel(c)} ${plainAmount(byCategory[c])}`);
  }
  if ((m.commission_aed_minor ?? 0) > 0) {
    terms.push(`${f("commission")} ${plainAmount(m.commission_aed_minor ?? 0)}`);
  }
  if ((m.extra_cost_aed_minor ?? 0) > 0) {
    terms.push(`${f("extraCost")} ${plainAmount(m.extra_cost_aed_minor ?? 0)}`);
  }
  const profit = m.profit_aed_minor ?? m.margin_aed_minor;
  const profitText = profit < 0 ? `−${plainAmount(-profit)}` : plainAmount(profit);
  return `${terms.join(" − ")} = ${f("profit")} ${profitText} AED`;
}

/** Who takes the client's money, in words: directly to us, our own driver (ours
 * at once), or a contractor who hands part of it over. */
function lineMoneyWho(
  line: BookingLineDetail,
  ownDriver: boolean,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (line.payment_collector !== "driver") return t("bookingLines.moneyDirect");
  if (ownDriver) return t("bookingLines.moneyOwnDriver");
  const guide = line.service_kind === "guide";
  const amount =
    line.collect_amount_minor != null
      ? formatMinor(line.collect_amount_minor, line.collect_currency || line.currency)
      : formatMinor(line.money.sale_native_minor, line.currency);
  const owes = line.money.driver_owes_aed_minor;
  return owes > 0
    ? t(guide ? "bookingLines.moneyGuideTakesGives" : "bookingLines.moneyDriverTakesGives", {
        amount,
        owes: formatMinor(owes, "AED"),
      })
    : t(guide ? "bookingLines.moneyGuideTakes" : "bookingLines.moneyDriverTakes", { amount });
}

export function lineLabel(line: BookingLineDetail): string {
  if (line.service_kind === "transfer") {
    const route = [line.from_location, line.to_location].filter(Boolean).join(" → ");
    const label = route ? `${tStatus("transfer")}: ${route}` : tStatus("transfer");
    return line.guide_included ? `${label} + ${i18n.t("bookingLines.guideShort")}` : label;
  }
  return line.service_name || tServiceKind(line.service_kind);
}

/** The Phase 13 "Lines" section on the booking detail page: list, add and
 * per-line costs. The booking's money totals live in the page's own card. */
function DriverSettlementStatus({
  line,
}: {
  bookingRef: string;
  line: BookingLineDetail;
  canConfirm: boolean;
}) {
  const { t } = useTranslation();
  // Status only: the money is marked received once, for the whole booking —
  // «Деньги получены» in the money card (no per-line confirm any more).
  if (
    line.driver_transfer_confirmed_at == null &&
    !line.driver_collected_at &&
    !line.driver_transfer_claimed_at
  ) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-olive-700 pt-2 text-xs text-sand-muted">
      {line.driver_collected_at && (
        <span>
          {t(line.service_kind === "guide" ? "bookingLines.guideCollected" : "bookingLines.driverCollected")}
        </span>
      )}
      {line.driver_transfer_confirmed_at ? (
        <span className="text-emerald-700">{t("bookingLines.transferConfirmed")}</span>
      ) : (
        <span>
          {line.driver_transfer_claimed_at
            ? t("bookingLines.transferClaimed")
            : t("bookingLines.transferNotClaimed")}
        </span>
      )}
    </div>
  );
}

/** Phase 14.3: the guide of a transfer («+ Гид») on its line card — who, language,
 * hours, the guide's own «гиду · продажа», who takes the client's money for the
 * guide (apart from the car) and, when the guide collects, the owner's
 * «деньги от гида получены» (owner rule 4). A missing guide is flagged. */
function TransferGuideInfo({
  line,
}: {
  bookingRef: string;
  line: BookingLineDetail;
  canConfirm: boolean;
}) {
  const { t } = useTranslation();
  const drivers = useDrivers();
  const guide = (drivers.data?.items ?? []).find((d) => d.id === line.guide_driver_id);
  const bits: string[] = [];
  if (line.guide_language) bits.push(line.guide_language.toUpperCase());
  if (line.guide_hours) bits.push(`${line.guide_hours} ${t("bookingLines.hoursShort")}`);
  bits.push(
    `${t("bookingLines.previewToGuide")} ${formatMinor(line.guide_pay_minor, "AED")} · ` +
      `${t("bookingLines.previewSaleLower")} ${formatMinor(line.guide_sale_minor, line.currency)}`,
  );
  const guideCollects = line.guide_payment_collector === "guide";
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2 border-t border-olive-700 pt-2 text-xs text-sand-muted"
      data-testid="transfer-guide-info"
    >
      <span className="font-semibold text-sand">{t("bookingLines.assignedGuide")}:</span>
      {line.guide_driver_id ? (
        <span className="text-sand">{guide?.name ?? "…"}</span>
      ) : (
        <span className="text-amber-700">{t("bookingLines.guideNotAssigned")}</span>
      )}
      <span>{bits.join(" · ")}</span>
      <span>
        {guideCollects ? t("bookingLines.collectorGuide") : t("bookingLines.collectorOwnerDirect")}
      </span>
      {guideCollects && line.guide_sale_minor > 0 && line.guide_transfer_confirmed_at && (
        <span className="text-emerald-700">{t("bookingLines.guideMoneyConfirmed")}</span>
      )}
    </div>
  );
}

/** Everything a line's completion moves: this booking (money card, status), the
 * booking list, home, finance, the dispatch board and the calendar. */
function lineMoneyKeys(bookingRef: string): unknown[][] {
  return [
    ["booking-lines", bookingRef],
    ["booking", bookingRef],
    ["bookings"],
    ["op-tasks"],
    ["calendar"],
    ["home"],
    ["finance"],
  ];
}

/** "Сафари, 18.09, Irina" — what the owner is about to mark done, in words. */
function lineSummaryText(line: BookingLineDetail, fallbackName: string): string {
  const day = formatDateOnly(line.service_date).replace(/[./-]\d{4}$/, "");
  return [lineLabel(line), day, line.passenger_name || fallbackName]
    .filter(Boolean)
    .join(", ");
}

/** «Выполнено»: asks first ("Отметить позицию выполненной? Сафари, 18.09, Irina" —
 * Да / Отмена), then the line's task jumps straight to completed (assign /
 * confirm / start skipped). Its money leaves "Ожидается" for "Заработано" and the
 * booking's derived status is recomputed on the server. A mis-tap is undone with
 * «Вернуть в работу» (`ReopenLineButton`). */
function CompleteLineButton({
  bookingRef,
  line,
  customerName,
}: {
  bookingRef: string;
  line: BookingLineDetail;
  customerName: string;
}) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const complete = useEntityAction(lineMoneyKeys(bookingRef), {
    inlineError: true,
    successMessage: t("bookingLines.completedToast"),
    onSuccess: () => setAsking(false),
  });
  const close = () => {
    complete.reset();
    setAsking(false);
  };
  return (
    <>
      <Button className="px-3 py-1 text-xs" onClick={() => setAsking(true)}>
        ✓ {t("bookingLines.complete")}
      </Button>
      {asking && (
        <Modal
          title={t("bookingLines.completeAsk")}
          size="sm"
          busy={complete.isPending}
          onClose={close}
        >
          <p className="text-sm text-sand">{lineSummaryText(line, customerName)}</p>
          {complete.isError && (
            <div className="mt-2">
              <ErrorNote message={(complete.error as Error).message} />
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button
              loading={complete.isPending}
              onClick={() =>
                complete.mutate(() =>
                  api.post(`/bookings/${bookingRef}/lines/${line.id}/complete`),
                )
              }
            >
              {t("bookingLines.completeYes")}
            </Button>
            <button
              type="button"
              className="text-sm text-sand-muted hover:text-sand disabled:opacity-50"
              disabled={complete.isPending}
              onClick={close}
            >
              {t("common.cancel")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/** «Вернуть в работу» — the undo of «Выполнено» or of a cancelled line (a mis-tap):
 * the task leaves `completed` / `cancelled`, the money is «Ожидается» again. Audited on the server. */
function ReopenLineButton({
  bookingRef,
  line,
}: {
  bookingRef: string;
  line: BookingLineDetail;
}) {
  const { t } = useTranslation();
  const reopen = useEntityAction(lineMoneyKeys(bookingRef), {
    successMessage: t("bookingLines.reopenedToast"),
  });
  return (
    <Button
      className="px-3 py-1 text-xs"
      loading={reopen.isPending}
      onClick={() =>
        reopen.mutate(() => api.post(`/bookings/${bookingRef}/lines/${line.id}/reopen`))
      }
    >
      {t("bookingLines.reopen")}
    </Button>
  );
}

/** The at-a-glance facts of a line (time, pax, tourist, where from/to) so the
 * owner does not have to open the editor to read them. */
/** A line cost's label. The automatic contractor cost of a yacht is the boat's
 * supplier and of a guide line the guide's pay — not a «Водитель». */
function lineCostLabel(
  category: string,
  serviceKind: string,
  t: (k: string) => string,
): string {
  // Phase 14.3: a transfer's guide — its own row, apart from the car.
  if (category === "guide") return t("bookingLines.guidePay");
  // Park tickets: bought from the ticket supplier — never the driver's pay.
  if (category === "entrance_tickets" && serviceKind === "park_tickets") {
    return t("bookingLines.ticketSupplierCost");
  }
  if (category === "driver_contractor") {
    if (serviceKind === "yacht") return t("bookingLines.yachtSupplierCost");
    if (serviceKind === "guide") return t("bookingLines.guidePay");
  }
  return tStatus(category);
}

function LineSummary({ line }: { line: BookingLineDetail }) {
  const { t } = useTranslation();
  const bits: string[] = [];
  if (line.pickup_time) {
    bits.push(
      `${t(line.service_kind === "guide" ? "bookingLines.guideStartTime" : "bookingLines.pickupTime")}: ${formatZonedTime(line.pickup_time)}`,
    );
  }
  if (line.adult_count != null || line.child_count != null) {
    // a group excursion / tickets: adults and children apart
    bits.push(
      `${t("bookingLines.adults")}: ${line.adult_count ?? 0}` +
        ((line.child_count ?? 0) > 0 ? ` · ${t("bookingLines.children")}: ${line.child_count}` : ""),
    );
  } else if (line.pax_count != null) {
    bits.push(`${t("bookingLines.pax")}: ${line.pax_count}`);
  }
  if (line.passenger_name) {
    bits.push(
      line.passenger_whatsapp
        ? `${line.passenger_name} (${line.passenger_whatsapp})`
        : line.passenger_name,
    );
  }
  if (line.service_kind === "yacht") {
    if (line.marina_location) bits.push(`${t("bookingLines.marinaLocation")}: ${line.marina_location}`);
    if (line.duration_hours) bits.push(`${t("bookingLines.rentalHours")}: ${line.duration_hours}`);
  } else if (line.service_kind === "guide") {
    if (line.duration_hours) bits.push(`${t("bookingLines.guideHours")}: ${line.duration_hours}`);
    if (line.guide_language) bits.push(line.guide_language.toUpperCase());
  } else if (line.service_kind !== "transfer") {
    const pickup = [line.pickup_location, line.pickup_address].filter(Boolean).join(" — ");
    const from = [pickup, line.from_emirate].filter(Boolean).join(", ");
    if (from) bits.push(`${t("bookingLines.pickupLocation")}: ${from}`);
    if (line.to_emirate) bits.push(`${t("bookingLines.destinationEmirate")}: ${line.to_emirate}`);
  }
  if (line.excursion_type) {
    bits.push(t(`bookingLines.excursionTypes.${line.excursion_type}`));
  } else if (
    line.transport_type &&
    line.transport_type !== "hourly" &&
    line.service_kind !== "guide" &&
    line.service_kind !== "park_tickets"
  ) {
    bits.push(
      line.transport_type === "private" ? t("bookingLines.privateCar") : tStatus(line.transport_type),
    );
  }
  if (bits.length === 0) return null;
  return <p className="mt-1 text-xs text-sand-muted">{bits.join(" · ")}</p>;
}

/** Assign / change the driver of one line right on its card (no trip to
 * Operations → Tasks): the current driver + their Telegram link status, a
 * driver picker, the "driver pay" (what NovaMoon pays them — the supplier
 * price the margin is computed from) and the Assign / Change button. */
export function LineDriverAssign({
  bookingRef,
  line,
  canAssign,
  canWrite,
  invalidate,
}: {
  bookingRef: string;
  line: BookingLineDetail;
  canAssign: boolean;
  canWrite: boolean;
  invalidate: unknown[][];
}) {
  const { t } = useTranslation();
  const drivers = useDrivers();
  const payField = driverPayField(line);
  const currentPay = fromMinor(line[payField]);
  const [driverId, setDriverId] = useState(line.driver_guide_id ?? "");
  const [pay, setPay] = useState(currentPay);
  const act = useEntityAction(invalidate, { inlineError: true });

  if (line.task_id == null) return null;
  // A yacht with no marina transfer has nobody to send (the backend's
  // line_needs_driver) — no driver block on its card.
  if (line.service_kind === "yacht" && !line.yacht_transfer_needed) return null;

  const all = drivers.data?.items ?? [];
  const assigned = all.find((d) => d.id === line.driver_guide_id);
  const terminal = line.task_status === "completed" || line.task_status === "cancelled";
  // A yacht's supplier price is a per-hour rate for the boat, not the driver's
  // pay — it is edited in the line form («Поставщику / час»), not here.
  // Nor a ticket line's: that is the ticket supplier's price, not the driver's pay.
  const canPay = canWrite && line.service_kind !== "yacht" && line.service_kind !== "park_tickets";
  const driverChanged = driverId !== "" && driverId !== (line.driver_guide_id ?? "");
  const payChanged = canPay && toMinor(pay) !== null && toMinor(pay) !== (toMinor(currentPay) ?? 0);
  const payInvalid = toMinor(pay) === null;
  const label = line.driver_guide_id
    ? driverChanged
      ? t("bookingLines.changeDriver")
      : t("bookingLines.savePay")
    : t("bookingLines.assignDriver");

  const run = () =>
    act.mutate(async () => {
      // Pay first: the assignment + payment-collection cards the new driver
      // gets are built from the line as it stands when they are queued.
      if (payChanged) {
        await api.patch(`/bookings/${bookingRef}/lines/${line.id}`, {
          [payField]: toMinor(pay),
        });
      }
      if (driverChanged) {
        await api.post(`/operational-tasks/${line.task_id}/assign`, {
          driver_guide_id: driverId,
        });
      }
    });

  const showPicker = canAssign && !terminal;
  const telegramBadge = line.driver_guide_id && assigned && (
    <span
      className={
        "rounded-full px-2 py-0.5 " +
        (assigned.telegram_user_id != null
          ? "bg-emerald-500/15 text-emerald-700"
          : "bg-olive-700 text-sand-muted")
      }
    >
      Telegram:{" "}
      {assigned.telegram_user_id != null
        ? t("drivers.telegramLinked")
        : t("drivers.telegramNotLinked")}
    </span>
  );

  return (
    <div
      className="mt-2 border-t border-olive-700 pt-2 text-xs"
      data-testid="line-driver"
    >
      {/* The driver is shown once: as read-only text when the card can't be
          edited, otherwise as the picker itself (its label carries the
          Telegram status) — never as a name *and* a picker showing that name. */}
      {!showPicker && (
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-semibold text-sand-muted">
            {t(line.service_kind === "guide" ? "bookingLines.assignedGuide" : "bookingLines.assignedDriver")}
          </h4>
          {line.driver_guide_id ? (
            <>
              <span className="text-sm text-sand">{assigned ? assigned.name : "…"}</span>
              {telegramBadge}
            </>
          ) : (
            <span className="text-sand-muted">{t("bookingLines.noDriver")}</span>
          )}
        </div>
      )}
      {showPicker && (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,10rem)_auto] sm:items-end">
          <Labeled
            label={
              <span className="flex flex-wrap items-center gap-2">
                {t(line.service_kind === "guide" ? "bookingLines.assignedGuide" : "bookingLines.assignedDriver")}
                {telegramBadge}
                {!line.driver_guide_id && (
                  <span className="normal-case text-sand-muted">
                    · {t("bookingLines.noDriver")}
                  </span>
                )}
              </span>
            }
          >
            <Select
              aria-label={t("bookingLines.pickDriver")}
              value={driverId}
              onChange={(e) => {
                setDriverId(e.target.value);
                // our own driver is not paid per trip: «Оплата водителю» → 0
                if (
                  payField === "supplier_price_per_vehicle_minor" &&
                  isOwnDriver(all.find((d) => d.id === e.target.value))
                ) {
                  setPay("");
                }
              }}
            >
              <option value="">—</option>
              {all
                .filter((d) => d.status === "active" || d.id === line.driver_guide_id)
                .filter((d) => line.service_kind !== "guide" || d.kind === "guide")
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {isOwnDriver(d) ? ` · ${t("drivers.own")}` : ""}
                    {d.telegram_user_id == null ? ` · ${t("bookingLines.noTelegramShort")}` : ""}
                  </option>
                ))}
            </Select>
          </Labeled>
          {canPay && (
            <Labeled
              label={
                payField === "supplier_price_per_person_minor"
                  ? t("bookingLines.driverPayPerPerson")
                  : line.service_kind === "guide"
                    ? t("bookingLines.guidePay")
                    : t("bookingLines.driverPay")
              }
            >
              <TextField
                type="number" min="0" step="0.01" placeholder="0.00"
                value={pay}
                onChange={(e) => setPay(e.target.value)}
              />
            </Labeled>
          )}
          <Button
            className="px-3 py-2 text-xs"
            loading={act.isPending}
            disabled={payInvalid || !(driverChanged || payChanged)}
            onClick={run}
          >
            {label}
          </Button>
        </div>
      )}
      {/* A driver with no Telegram gets the task by hand, through WhatsApp. */}
      {canAssign && !terminal && line.driver_guide_id && assigned && assigned.telegram_user_id == null && (
        <div className="mt-2">
          <WhatsAppSend
            taskId={line.task_id}
            sentAt={line.task_whatsapp_sent_at}
            invalidate={invalidate}
          />
        </div>
      )}
      {act.isError && (
        <div className="mt-1">
          <ErrorNote message={(act.error as Error).message} />
        </div>
      )}
    </div>
  );
}

export function BookingLinesSection({
  bookingRef,
  currency,
  canWrite,
  canWriteCosts,
  canAssign = false,
  canComplete = false,
  bookingStatus = "",
  customerName = "",
}: {
  bookingRef: string;
  currency: string;
  canWrite: boolean;
  canWriteCosts: boolean;
  canAssign?: boolean;
  /** `bookings:write` + `operations:write` — completing a line closes its dispatch task. */
  canComplete?: boolean;
  /** The booking's stored status: a draft / cancelled / refunded booking earns nothing,
   * so its lines cannot be marked done. */
  bookingStatus?: string;
  /** The order's customer — pre-fills each new line's tourist name. */
  customerName?: string | null;
}) {
  const { t } = useTranslation();
  const drivers = useDrivers();
  const ownDriverOf = (line: BookingLineDetail) =>
    isOwnDriver(drivers.data?.items.find((d) => d.id === line.driver_guide_id));
  const customer = customerName ?? "";
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState(() => emptyLineDraft(currency, customer));
  // A line edit / driver change moves the booking's profit, the dispatch
  // tasks and the calendar feed too, so refresh all of them.
  const invalidate: unknown[][] = [
    ["booking-lines", bookingRef],
    ["booking", bookingRef],
    ["op-tasks"],
    ["calendar"],
    // a park typed on a ticket line joins the «Парк / аттракцион» list
    ["ticket-attractions"],
  ];
  const add = useEntityAction(invalidate, { inlineError: true,
    onSuccess: () => {
      setAdding(false);
      setDraft(emptyLineDraft(currency, customer));
    },
  });
  const remove = useEntityAction(invalidate, { successMessage: t("common.deleted") });

  const bookingLive = !["draft", "cancelled", "refunded"].includes(bookingStatus);
  // Dubai's today, not the browser's: a trip that has not happened yet cannot be
  // done (the server refuses it too).
  const today = companyToday();
  const showComplete = (line: BookingLineDetail) =>
    canComplete &&
    bookingLive &&
    line.service_date <= today &&
    line.task_status !== "completed" &&
    line.task_status !== "cancelled";
  const showReopen = (line: BookingLineDetail) =>
    canComplete &&
    bookingLive &&
    (line.task_status === "completed" || line.task_status === "cancelled");

  const linesQuery = useQuery({
    queryKey: ["booking-lines", bookingRef],
    queryFn: () =>
      api.get<BookingLineDetail[]>(`/bookings/${bookingRef}/lines`),
  });
  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <h2 className="text-sm font-semibold text-sand">{t("bookingLines.title")}</h2>
        {canWrite && !adding && (
          <Button className="ml-auto px-3 py-1 text-xs" onClick={() => setAdding(true)}>
            + {t("bookingLines.add")}
          </Button>
        )}
      </div>

      {adding && (
        <Card>
          <div className="mb-2 text-sm font-semibold">{t("bookingLines.addTitle")}</div>
          <LineFields value={draft} onChange={(p) => setDraft({ ...draft, ...p })} />
          {add.isError && (
            <div className="mt-2">
              <ErrorNote message={(add.error as Error).message} />
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <Button
              loading={add.isPending}
              disabled={!lineDraftIsValid(draft)}
              onClick={() =>
                add.mutate(() =>
                  api.post(
                    `/bookings/${bookingRef}/lines`,
                    lineDraftToPayload(draft, currency),
                  ),
                )
              }
            >
              {t("common.save")}
            </Button>
            <button
              className="text-sm text-sand-muted hover:text-sand"
              onClick={() => setAdding(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </Card>
      )}

      {linesQuery.isLoading ? (
        <Spinner />
      ) : linesQuery.isError ? (
        <ErrorNote message={(linesQuery.error as Error).message} />
      ) : linesQuery.data!.length === 0 ? (
        <p className="mt-2 text-sm text-sand-muted">{t("bookingLines.noLines")}</p>
      ) : (
        <div className="mt-3 space-y-3">
          {linesQuery.data!.map((line) => (
            <Card key={line.id}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-medium">{lineLabel(line)}</span>
                <span className="text-sand-muted">
                  {formatDateOnly(line.service_date)}
                </span>
                {/* A yacht with no marina transfer has nobody to assign — its
                    "unassigned" task is not news (only done / cancelled is). */}
                {line.task_status &&
                  !(
                    line.service_kind === "yacht" &&
                    !line.yacht_transfer_needed &&
                    line.task_status === "unassigned"
                  ) && <StatusBadge status={line.task_status} />}
                <span className="text-sand-muted" data-testid="line-money-who">
                  {lineMoneyWho(line, ownDriverOf(line), t)}
                </span>
                {(canWrite || showComplete(line) || showReopen(line)) && (
                  <span className="ml-auto flex items-center gap-3">
                    {showComplete(line) && (
                      <CompleteLineButton
                        bookingRef={bookingRef}
                        line={line}
                        customerName={customer}
                      />
                    )}
                    {showReopen(line) && (
                      <ReopenLineButton bookingRef={bookingRef} line={line} />
                    )}
                    {canWrite && (
                      <Button
                        className="px-3 py-1 text-xs"
                        onClick={() => setEditingId(line.id)}
                      >
                        {t("common.edit")}
                      </Button>
                    )}
                    {canWrite && (
                      <ConfirmButton
                        label={t("common.delete")}
                        busy={remove.isPending}
                        onConfirm={() =>
                          remove.mutate(() =>
                            api.del(`/bookings/${bookingRef}/lines/${line.id}`),
                          )
                        }
                      />
                    )}
                  </span>
                )}
              </div>
              {/* the line's money in one plain sentence (owner request) */}
              <p className="mt-1 text-sm font-semibold text-ink" data-testid="line-formula">
                {lineFormula(line, t)}
              </p>
              <LineSummary line={line} />
              {line.guide_included && (
                <TransferGuideInfo
                  bookingRef={bookingRef}
                  line={line}
                  canConfirm={canWriteCosts}
                />
              )}
              <LineDriverAssign
                key={[
                  line.id,
                  line.driver_guide_id,
                  line.supplier_price_per_person_minor,
                  line.supplier_price_per_vehicle_minor,
                ].join(":")}
                bookingRef={bookingRef}
                line={line}
                canAssign={canAssign}
                canWrite={canWrite}
                invalidate={invalidate}
              />
              {/* our own driver's cash is ours already — nothing to confirm */}
              {line.payment_collector === "driver" && !ownDriverOf(line) && (
                <DriverSettlementStatus
                  bookingRef={bookingRef}
                  line={line}
                  canConfirm={canWriteCosts}
                />
              )}
              <LineCosts bookingRef={bookingRef} line={line} canWrite={canWriteCosts} />
            </Card>
          ))}
        </div>
      )}
      {editingId && linesQuery.data?.find((l) => l.id === editingId) && (
        <EditBookingLineModal
          bookingRef={bookingRef}
          line={linesQuery.data.find((l) => l.id === editingId)!}
          currency={currency}
          customerName={customer}
          invalidate={invalidate}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
